import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Loader2, Trash2, Users, Banknote, Clock, Calendar, Wallet, Lock } from 'lucide-react';
import { payrollApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { cn, formatCurrency } from '../../lib/utils';
import { useCurrencyStore } from '../../stores/currencyStore';

interface ProfileFormData {
  employee_id: string;
  pay_frequency: string;
  pay_type: string;
  base_salary: number;
  currency: string;
  bank_account: string;
}

const EmployeeProfiles: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currencyCode } = useCurrencyStore();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<any>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    employee_id: '',
    pay_frequency: 'MONTHLY',
    pay_type: 'SALARY',
    base_salary: 0,
    currency: currencyCode,
    bank_account: '',
  });

  const { data: profiles = [], isLoading, error } = useQuery({
    queryKey: ['payrollProfiles'],
    queryFn: () => payrollApi.profiles.getAll({}),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  const createMutation = useMutation({
    mutationFn: payrollApi.profiles.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollProfiles'] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: 'Payroll profile created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => payrollApi.profiles.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollProfiles'] });
      setIsDialogOpen(false);
      setEditingProfile(null);
      resetForm();
      toast({ title: 'Payroll profile updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => payrollApi.profiles.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollProfiles'] });
      toast({ title: 'Payroll profile deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleDelete = (profile: any) => {
    if (window.confirm(`Are you sure you want to delete the payroll profile for ${getEmployeeName(profile.employee_id)}?`)) {
      deleteMutation.mutate(profile.id);
    }
  };

  const handleSubmit = () => {
    if (!formData.employee_id || !formData.base_salary) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    if (editingProfile) {
      updateMutation.mutate({ id: editingProfile.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const openDialog = (profile?: any) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        employee_id: profile.employee_id,
        pay_frequency: profile.pay_frequency,
        pay_type: profile.pay_type,
        base_salary: profile.base_salary,
        currency: profile.currency,
        bank_account: profile.bank_account || '',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      pay_frequency: 'MONTHLY',
      pay_type: 'SALARY',
      base_salary: 0,
      currency: currencyCode,
      bank_account: '',
    });
    setEditingProfile(null);
  };

  const getEmployeeName = (employeeId: string) => {
    const emp = employees.find((e: any) => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name || ''}`.trim() : 'Unknown';
  };

  const getEmployeeInitials = (name: string) => {
    const parts = name.trim().split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getPayTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      SALARY:   'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
      HOURLY:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
      CONTRACT: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800',
    };
    const classNames = colors[type?.toUpperCase()] || 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0", classNames)} variant="outline">
        {type}
      </Badge>
    );
  };

  const getFrequencyBadge = (freq: string) => {
    const colors: Record<string, string> = {
      WEEKLY:    'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800',
      BIWEEKLY:  'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-400 dark:border-sky-800',
      MONTHLY:   'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700',
    };
    const classNames = colors[freq?.toUpperCase()] || 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0", classNames)} variant="outline">
        {freq}
      </Badge>
    );
  };

  const totalProfilesCount = profiles.length;
  const hourlyCount = profiles.filter((p: any) => p.pay_type === 'HOURLY').length;
  const salaryCount = profiles.filter((p: any) => p.pay_type === 'SALARY').length;
  const totalBaseMonthly = profiles.reduce((sum: number, p: any) => sum + p.base_salary, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading payroll profiles...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-rose-50 border border-rose-100 text-rose-700 px-4 py-3 rounded-2xl flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-rose-500" />
          <div>
            <p className="font-bold">Error loading profiles</p>
            <p className="text-xs font-semibold mt-0.5 opacity-90">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header section with inline action */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Manage employee compensation details</h2>
          
        </div>
        
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Button 
            onClick={() => openDialog()} 
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Profile
          </Button>
        </div>
      </div>

      {/* Payroll Profiles List Table */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Payroll Profiles</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Employee</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Pay Type</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Frequency</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Base Salary</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Currency</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Bank Account</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-20 px-8">
                    <div className="flex flex-col items-center">
                      <div className="bg-slate-50 dark:bg-slate-800 h-14 w-14 rounded-2xl flex items-center justify-center mb-4 border border-slate-100 dark:border-slate-700">
                        <Wallet className="h-7 w-7 text-slate-350 dark:text-slate-650" />
                      </div>
                      <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">No payroll profiles found</h3>
                      <p className="text-xs font-semibold text-slate-400 mt-1 max-w-sm">
                        Create your first employee compensation profile to get started with runs.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                profiles.map((profile: any) => {
                  const empName = getEmployeeName(profile.employee_id);
                  const initials = getEmployeeInitials(empName);
                  return (
                    <TableRow key={profile.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                      <TableCell className="px-8 py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] dark:text-blue-400 font-bold text-[9px] flex items-center justify-center border border-slate-100/50 shrink-0">
                            {initials}
                          </div>
                          <span className="font-bold text-xs text-slate-700 dark:text-slate-300">{empName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        {getPayTypeBadge(profile.pay_type)}
                      </TableCell>
                      <TableCell className="py-4">
                        {getFrequencyBadge(profile.pay_frequency)}
                      </TableCell>
                      <TableCell className="text-xs font-black text-slate-800 dark:text-slate-200 py-4">
                        {formatCurrency(profile.base_salary)}
                      </TableCell>
                      <TableCell className="text-xs font-bold text-slate-500 py-4">
                        {profile.currency}
                      </TableCell>
                      <TableCell className="text-xs font-semibold text-slate-500 py-4">
                        {profile.bank_account || <span className="text-slate-350 italic">Not Configured</span>}
                      </TableCell>
                      <TableCell className="px-8 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openDialog(profile)}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(profile)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Profile Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Wallet className="h-5 w-5 text-blue-500" />
              <span>{editingProfile ? 'Edit Payroll Profile' : 'Create Payroll Profile'}</span>
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure compensation and routing parameters
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="employee_id" className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              <Select
                value={formData.employee_id}
                onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="border-slate-100 dark:border-slate-800">
                  {employees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id} className="text-sm font-bold">
                      {emp.first_name} {emp.last_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pay_type" className="text-xs font-bold text-slate-400 tracking-wider">Pay Type</Label>
                <Select
                  value={formData.pay_type}
                  onValueChange={(value) => setFormData({ ...formData, pay_type: value })}
                >
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    <SelectItem value="SALARY" className="text-sm font-bold">Salary</SelectItem>
                    <SelectItem value="HOURLY" className="text-sm font-bold">Hourly</SelectItem>
                    <SelectItem value="CONTRACT" className="text-sm font-bold">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pay_frequency" className="text-xs font-bold text-slate-400 tracking-wider">Frequency</Label>
                <Select
                  value={formData.pay_frequency}
                  onValueChange={(value) => setFormData({ ...formData, pay_frequency: value })}
                >
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    <SelectItem value="WEEKLY" className="text-sm font-bold">Weekly</SelectItem>
                    <SelectItem value="BIWEEKLY" className="text-sm font-bold">Bi-Weekly</SelectItem>
                    <SelectItem value="MONTHLY" className="text-sm font-bold">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="base_salary" className="text-xs font-bold text-slate-400 tracking-wider">Base Salary *</Label>
                <Input
                  id="base_salary"
                  type="number"
                  value={formData.base_salary}
                  onChange={(e) => setFormData({ ...formData, base_salary: parseFloat(e.target.value) })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="currency" className="text-xs font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                  Currency
                  <Lock className="h-3 w-3 text-slate-400" />
                </Label>
                <div
                  id="currency"
                  title="Currency is locked to the system currency. Change it in Settings."
                  className="flex items-center gap-2 h-11 px-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 cursor-not-allowed"
                >
                  <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="font-bold text-sm text-slate-600 dark:text-slate-300">{currencyCode}</span>
                  <span className="ml-auto text-xs text-slate-400">System currency</span>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bank_account" className="text-xs font-bold text-slate-400 tracking-wider">Bank Account</Label>
              <Input
                id="bank_account"
                value={formData.bank_account}
                onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                placeholder="Account number"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm h-11"
              />
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingProfile ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeProfiles;